<script setup lang="ts">
/**
 * Refunds queue container.
 *
 * Owns:
 *   - status-counts query (header count + sidebar badge)
 *   - list query — page-mount default locked to PENDING (only actionable state)
 *   - filter drawer state + URL sync
 *
 * Drawer chrome + detail query + approve/reject mutations + confirm
 * dialogs live in `RefundsDetailDrawerContainer.vue` (per
 * `architecture-principles.md §20` drawer-container split).
 */
import { computed, ref } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'

import { useFilterUrlSync } from '@shared/composables/useFilterUrlSync'
import { dateToIso, formatToInternational } from '@shared/utils'

import RefundsFilterDrawer, {
  type RefundsFilterValues,
} from '../components/RefundsFilterDrawer.vue'
import RefundsList from '../components/RefundsList.vue'
import RefundsDetailDrawerContainer from './RefundsDetailDrawerContainer.vue'

import { useRefundsListQuery } from '../api/queries/useRefundsListQuery'
import { useRefundStatusCountsQuery } from '../api/queries/useRefundStatusCountsQuery'

import { REFUND_STATE, type GetRefundsParams, type RefundRow } from '../types/refundsTypes'

// ─── Filters + pagination (server-side) ─────────────────────────────────────

const currentPage = ref(1)
const pageSize = ref(10)

const filterValues = ref<RefundsFilterValues>({
  fromDate: null,
  toDate: null,
  investorName: '',
  investorEmail: '',
})

const filterDrawerVisible = ref(false)

useFilterUrlSync(filterValues, {
  toQuery: (state) => ({
    fromDate: state.fromDate ? state.fromDate.toISOString() : undefined,
    toDate: state.toDate ? state.toDate.toISOString() : undefined,
    investorName: state.investorName || undefined,
    investorEmail: state.investorEmail || undefined,
  }),
  fromQuery: (query) => {
    const seed: Partial<RefundsFilterValues> = {}
    if (typeof query.fromDate === 'string') {
      const d = new Date(query.fromDate)
      if (!Number.isNaN(d.getTime())) seed.fromDate = d
    }
    if (typeof query.toDate === 'string') {
      const d = new Date(query.toDate)
      if (!Number.isNaN(d.getTime())) seed.toDate = d
    }
    if (typeof query.investorName === 'string') seed.investorName = query.investorName
    if (typeof query.investorEmail === 'string') seed.investorEmail = query.investorEmail
    return seed
  },
  onFiltersChange: () => {
    currentPage.value = 1
  },
})

const listParams = computed<GetRefundsParams>(() => {
  const params: GetRefundsParams = {
    page: currentPage.value,
    limit: pageSize.value,
    refundState: REFUND_STATE.PENDING,
  }
  const fromIso = dateToIso(filterValues.value.fromDate)
  if (fromIso) params.fromDate = fromIso
  const toIsoDate = dateToIso(filterValues.value.toDate)
  if (toIsoDate) params.toDate = toIsoDate
  if (filterValues.value.investorName) params.investorName = filterValues.value.investorName
  if (filterValues.value.investorEmail) params.investorEmail = filterValues.value.investorEmail
  return params
})

const {
  data: listData,
  isLoading: listLoading,
  isFetching: listFetching,
  error: listError,
  refetch: refetchList,
} = useRefundsListQuery(listParams)

const { data: statusCounts, isLoading: countsLoading } = useRefundStatusCountsQuery()

const pendingCount = computed(() => statusCounts.value?.pending ?? 0)
const headerCountLabel = computed(() => {
  if (countsLoading.value) return '…'
  return `${formatToInternational(pendingCount.value)} pending`
})

function onFiltersApply(next: RefundsFilterValues) {
  filterValues.value = { ...next }
  currentPage.value = 1
}

function onFiltersReset() {
  filterValues.value = {
    fromDate: null,
    toDate: null,
    investorName: '',
    investorEmail: '',
  }
  currentPage.value = 1
}

const activeFilterCount = computed(() => {
  const f = filterValues.value
  let count = 0
  if (f.fromDate) count += 1
  if (f.toDate) count += 1
  if (f.investorName) count += 1
  if (f.investorEmail) count += 1
  return count
})

const hasActiveFilters = computed(() => activeFilterCount.value > 0)

const emptyTitle = computed(() =>
  hasActiveFilters.value ? 'No refund requests match your filters' : 'No refunds to review',
)
const emptySubtitle = computed(() =>
  hasActiveFilters.value
    ? 'Clear filters to see all pending refunds.'
    : 'Refund requests appear here as soon as an order is rejected by admin or canceled by an investor.',
)

function onSearchChange(value: string) {
  filterValues.value = { ...filterValues.value, investorName: value }
  currentPage.value = 1
}

function openFilterDrawer() {
  filterDrawerVisible.value = true
}

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

// ─── Detail drawer handoff ──────────────────────────────────────────────────

const selectedRow = ref<RefundRow | null>(null)
function openRow(row: RefundRow) {
  selectedRow.value = row
}
function closeDrawer() {
  selectedRow.value = null
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load refund requests. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <RefundsList
      title="Refund approvals"
      :subtitle="headerCountLabel"
      :rows="listData?.data ?? []"
      :loading="listLoading || listFetching"
      :total="listData?.meta.total ?? pendingCount"
      :page="currentPage"
      :page-size="pageSize"
      :filter-count="activeFilterCount"
      :search="filterValues.investorName"
      :empty-title="emptyTitle"
      :empty-subtitle="emptySubtitle"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @update:search="onSearchChange"
      @filter-click="openFilterDrawer"
      @row-click="openRow"
    />
  </Box>

  <RefundsFilterDrawer
    :visible="filterDrawerVisible"
    :values="filterValues"
    @update:visible="filterDrawerVisible = $event"
    @apply="onFiltersApply"
    @reset="onFiltersReset"
  />

  <RefundsDetailDrawerContainer :selected-row="selectedRow" @close="closeDrawer" />
</template>
